import type { Column, Row, Table } from '@tanstack/react-table';
import { isNil } from 'es-toolkit';

import type { ExportableColumn, ExportRow } from '../../types/export';

/**
 * Resolves the export header for a column.
 */
function getExportHeader<TData>(column: Column<TData>): string {
  const meta = column.columnDef.meta;

  if (!isNil(meta?.export?.header)) {
    return meta.export.header;
  }

  const header = column.columnDef.header;

  if (typeof header === 'string') {
    return header;
  }

  return column.id;
}

/**
 * Converts TanStack columns to ExportableColumn format.
 */
function toExportableColumn<TData>(column: Column<TData>, isVisible: boolean): ExportableColumn {
  return {
    id: column.id,
    header: getExportHeader(column),
    isVisible,
  };
}

/**
 * Gets exportable columns from the table, respecting column meta and visibility.
 * Columns with `meta.export.additionalColumns` are expanded into multiple export columns.
 */
export function getExportableColumns<TData>(table: Table<TData>): ExportableColumn[] {
  const visibleColumnIds = new Set(table.getVisibleLeafColumns().map((c) => c.id));
  const result: ExportableColumn[] = [];

  for (const column of table.getAllLeafColumns()) {
    if (column.columnDef.meta?.export?.enabled === false) continue;
    result.push(toExportableColumn(column, visibleColumnIds.has(column.id)));

    const additional = column.columnDef.meta?.export?.additionalColumns;
    if (additional) {
      for (const addCol of additional) {
        result.push({
          id: addCol.id,
          header: addCol.header,
          isVisible: addCol.isVisible ?? visibleColumnIds.has(column.id),
        });
      }
    }
  }

  return result;
}

/**
 * Resolves the export value for a cell.
 */
export function getExportValue<TData>(column: Column<TData>, row: Row<TData>): string | number | null {
  const meta = column.columnDef.meta;
  const value = row.getValue(column.id);

  if (meta?.export?.value) {
    return meta.export.value(value, row.original);
  }

  if (isNil(value)) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  // Fallback: attempt JSON stringification
  return JSON.stringify(value, null, 2);
}

/**
 * Builds a map from export column ID to its value resolver.
 * Handles both primary columns and additional columns defined in meta.
 */
function buildValueResolvers<TData>(table: Table<TData>): Map<string, (row: Row<TData>) => string | number | null> {
  const resolvers = new Map<string, (row: Row<TData>) => string | number | null>();

  for (const column of table.getAllLeafColumns()) {
    if (column.columnDef.meta?.export?.enabled === false) continue;
    resolvers.set(column.id, (row) => getExportValue(column, row));

    const additional = column.columnDef.meta?.export?.additionalColumns;
    if (additional) {
      for (const addCol of additional) {
        resolvers.set(addCol.id, (row) => {
          const cellValue = row.getValue(column.id);
          return addCol.value(cellValue, row.original);
        });
      }
    }
  }

  return resolvers;
}

/**
 * Extracts export rows from the table.
 */
export function getExportRows<TData>(table: Table<TData>, columnIds: string[]): ExportRow[] {
  const resolvers = buildValueResolvers(table);

  return table
    .getFilteredRowModel()
    .flatRows.map((row) => Object.fromEntries(columnIds.map((id) => [id, resolvers.get(id)?.(row) ?? null])));
}

/**
 * Gets headers for export columns.
 */
export function getExportHeaders<TData>(table: Table<TData>, columnIds: string[]): string[] {
  const headerMap = new Map(getExportableColumns(table).map((col) => [col.id, col.header]));
  return columnIds.map((id) => headerMap.get(id) ?? id);
}
